import { useCallback, useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { FormField } from '@/components/ui/form';
import { Button } from '@/ui-lib/button';
import { renderNumberInputFactory } from '@/ui-lib/form/form-input-renders';

import Icon from './Icon';
import { CalculatorInput } from './Inputs';
import { MultiModeInput } from './schema';
import { SimpleCalculatorFormData } from './types';

interface CommissionFieldProps {
    form: UseFormReturn<SimpleCalculatorFormData> | UseFormReturn<MultiModeInput>;
    disabled?: boolean;
}

export function CommissionField({ form, disabled }: CommissionFieldProps) {
    const renderInput = useMemo(() => renderNumberInputFactory({ suffix: '%', min: 0.01, max: 100 }), []);

    // The input is mounted lazily (only once a value exists), so we can't focus it
    // synchronously on click. A callback ref focuses + selects the moment it mounts.
    const pendingFocus = useRef(false);
    const focusOnMount = useCallback((node: HTMLInputElement | null) => {
        if (node && pendingFocus.current) {
            pendingFocus.current = false;
            node.focus();
            node.select();
        }
    }, []);

    return (
        <FormField
            control={form.control as any}
            name="commissionPct"
            render={({ field: labelField, fieldState: { error } }) =>
                labelField.value === null || labelField.value === undefined ? (
                    <Button
                        variant="text"
                        onClick={() => {
                            pendingFocus.current = true;
                            labelField.onChange('');
                        }}
                    >
                        Add Commission
                    </Button>
                ) : (
                    <CalculatorInput
                        type="text"
                        label={labelField.value === null || labelField.value === undefined ? '' : 'Commission %'}
                        labelClassName="mt-1"
                        plainLabel
                        isRequired={false}
                        placeholder="Commission %"
                        inputProps={{
                            ...labelField,
                            value: labelField.value ?? '',
                            ref: focusOnMount,
                        }}
                        onChange={labelField.onChange}
                        error={error?.message}
                        disabled={disabled}
                        tabIndex={0}
                        suffix={
                            <button
                                type="button"
                                tabIndex={-1}
                                aria-label="Clear commission"
                                disabled={disabled}
                                onClick={() => labelField.onChange(null)}
                                className="flex items-center text-frGrey-400 hover:text-frGrey-200 disabled:pointer-events-none"
                            >
                                <Icon icon="X" width={16} height={16} viewBox="0 0 24 24" className="h-4 w-4" />
                            </button>
                        }
                    />
                )
            }
        />
    );
}
